import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Form } from 'react-bootstrap';
import { usePOS } from 'contexts/POSContext';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { usePrinterStore } from 'stores/printerStore';
import { ROUTES } from 'utils/constants';
import { Product, Customer, CreateSalesRequest } from 'types/sales';
import {
  printReceipt,
  ReceiptData,
  requestBluetoothDevice,
  requestUsbDevice,
} from 'utils/printerUtils';
import Button from 'components/Button';
import Toast from 'components/Toast';
import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  X,
  CheckCircle2,
  Circle,
  CreditCard,
  Package,
  Clock,
  Ban,
  CheckSquare,
  History,
  Printer,
  Bluetooth,
  Usb,
} from 'lucide-react';
import AddCustomerModal from 'pages/customers/AddCustomerModal';
import OrdersListModal from './OrdersListModal';
import OrderTypeSelectionModal from './OrderTypeSelectionModal';
import {
  storeFloatingOrder,
  getAllFloatingOrders,
  deleteFloatingOrder,
  getAllVoidedOrders,
  getAllCompletedOrders,
  storeVoidedOrder,
  deleteCompletedOrder,
  updateCompletedOrderSyncStatus,
  getPendingTransaction,
  FloatingOrder,
  VoidedOrder,
  CompletedOrder,
  PendingTransaction,
} from 'utils/posIndexedDBUtils';
import styles from './POS.module.scss';

function POS() {
  const navigate = useNavigate();

  // Get active company ID for filtering transactions
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);

  // Use POS context for sales options (shared across POS pages)
  const {
    products,
    customers,
    paymentMethods,
    loading,
    error: salesOptionsError,
    reload,
    cart,
    setCart,
    selectedCustomer,
    setSelectedCustomer,
    customerSearchQuery,
    setCustomerSearchQuery,
  } = usePOS();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isCustomerInputFocused, setIsCustomerInputFocused] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const customerSearchRef = useRef<HTMLDivElement>(null);
  const [quantityEditModal, setQuantityEditModal] = useState<{
    show: boolean;
    productId: string | null;
    productName: string;
    currentQuantity: number;
  }>({
    show: false,
    productId: null,
    productName: '',
    currentQuantity: 0,
  });
  const [showQuantityNumpad, setShowQuantityNumpad] = useState(false);
  const [tempQuantity, setTempQuantity] = useState('');
  const [isInitialQuantityDefault, setIsInitialQuantityDefault] =
    useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [showOrdersModal, setShowOrdersModal] = useState<{
    show: boolean;
    type: 'floating' | 'voided' | 'completed';
  }>({ show: false, type: 'floating' });
  const [floatingOrders, setFloatingOrders] = useState<FloatingOrder[]>([]);
  const [voidedOrders, setVoidedOrders] = useState<VoidedOrder[]>([]);
  const [completedOrders, setCompletedOrders] = useState<CompletedOrder[]>([]);
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [showPrinterQuickSettings, setShowPrinterQuickSettings] =
    useState(false);
  const [isTestPrinting, setIsTestPrinting] = useState(false);

  const printerSettings = usePrinterStore((s) => s.printerSettings);
  const updatePrinterSettings = usePrinterStore((s) => s.updatePrinterSettings);
  const savedPrinter = usePrinterStore((s) => s.savedPrinter);
  const setSavedPrinter = usePrinterStore((s) => s.setSavedPrinter);
  const activeDevice = usePrinterStore((s) => s.activeDevice);
  const setActiveDevice = usePrinterStore((s) => s.setActiveDevice);

  const handleConnectBluetooth = async () => {
    try {
      const device = await requestBluetoothDevice();
      if (device) {
        const newPrinter = {
          type: 'bluetooth' as const,
          id: device.id,
          name: device.name || 'Unknown Printer',
        };
        void setSavedPrinter(newPrinter);
        setActiveDevice(device);
        showToast(`Connected to ${newPrinter.name}`, 'success');
      }
    } catch (err) {
      if ((err as Error).name !== 'NotFoundError') {
        showToast((err as Error).message, 'error');
      }
    }
  };

  const handleConnectUsb = async () => {
    try {
      const device = await requestUsbDevice();
      if (device) {
        const newPrinter = {
          type: 'usb' as const,
          id: `${device.vendorId}_${device.productId}`,
          name: device.productName || 'USB Printer',
        };
        void setSavedPrinter(newPrinter);
        setActiveDevice(device);
        showToast(`Connected to ${newPrinter.name}`, 'success');
      }
    } catch (err) {
      if ((err as Error).name !== 'NotFoundError') {
        showToast((err as Error).message, 'error');
      }
    }
  };

  const handleForgetPrinter = () => {
    void setSavedPrinter(null);
    setActiveDevice(null);
    showToast('Printer disconnected', 'success');
  };

  const handleQuickTestPrint = async () => {
    if (!savedPrinter) return;

    setIsTestPrinting(true);
    try {
      const testData: ReceiptData = {
        items: [],
        total: 0,
        paymentMethod: 'Test',
        amountReceived: 0,
        change: 0,
        shouldCut: printerSettings.autoCut,
      };

      const newDevice = await printReceipt(
        testData,
        savedPrinter,
        activeDevice,
      );
      if (newDevice) setActiveDevice(newDevice);
      showToast('Test print sent!', 'success');
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setIsTestPrinting(false);
    }
  };

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Show error toast if sales options failed to load
  useEffect(() => {
    if (salesOptionsError) {
      showToast(salesOptionsError, 'error');
    }
  }, [salesOptionsError, showToast]);

  // Find default customer
  const defaultCustomer = useMemo(() => {
    return customers.find((c) => c.name === 'Default Customer');
  }, [customers]);

  // Set default customer on load if none selected
  useEffect(() => {
    if (customers.length > 0 && !selectedCustomer) {
      const defCustomer = customers.find((c) => c.name === 'Default Customer');
      if (defCustomer) {
        setSelectedCustomer(defCustomer);
        setCustomerSearchQuery(defCustomer.name);
      }
    }
  }, [
    customers,
    selectedCustomer,
    setSelectedCustomer,
    setCustomerSearchQuery,
  ]);

  // Clear local order lists when company changes
  useEffect(() => {
    if (activeCompanyId) {
      setFloatingOrders([]);
      setVoidedOrders([]);
      setCompletedOrders([]);
    }
  }, [activeCompanyId]);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customerSearchRef.current &&
        !customerSearchRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCustomerDropdown]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    // If input is empty and focused, show default customer
    if (
      !customerSearchQuery.trim() &&
      isCustomerInputFocused &&
      defaultCustomer
    ) {
      return [defaultCustomer];
    }
    // If input has text, filter customers
    if (customerSearchQuery.trim()) {
      const searchLower = customerSearchQuery.toLowerCase();
      return customers.filter(
        (customer) =>
          customer.name.toLowerCase().includes(searchLower) ||
          customer.email?.toLowerCase().includes(searchLower) ||
          customer.phone?.toLowerCase().includes(searchLower),
      );
    }
    return [];
  }, [customers, customerSearchQuery, isCustomerInputFocused, defaultCustomer]);

  // Get unique categories from products
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    products.forEach((product) => {
      if (product.productCategory) {
        categorySet.add(product.productCategory);
      }
    });
    return Array.from(categorySet).sort();
  }, [products]);

  // Filter products based on search and selected category
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filter by selected category
    if (selectedCategory) {
      filtered = filtered.filter(
        (product) => product.productCategory === selectedCategory,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(searchLower),
      );
    }

    return filtered;
  }, [products, searchQuery, selectedCategory]);

  // Group products by category
  const groupedProducts = useMemo(() => {
    const groups: Record<string, typeof products> = {};
    filteredProducts.forEach((product) => {
      const category = product.productCategory || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(product);
    });
    return groups;
  }, [filteredProducts]);

  // Add product to cart
  const addToCart = useCallback(
    (product: Product) => {
      setCart((prevCart) => {
        const existingItem = prevCart.find(
          (item) => item.productId === product.id,
        );
        if (existingItem) {
          return prevCart.map((item) =>
            item.productId === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item,
          );
        }
        return [
          ...prevCart,
          {
            productId: product.id,
            productName: product.name,
            price:
              typeof product.price === 'string'
                ? Number(product.price)
                : product.price,
            quantity: 1,
          },
        ];
      });
    },
    [setCart],
  );

  // Remove from cart
  const removeFromCart = useCallback(
    (productId: string) => {
      setCart((prevCart) =>
        prevCart.filter((item) => item.productId !== productId),
      );
    },
    [setCart],
  );

  // Update cart item quantity
  const updateCartQuantity = useCallback(
    (productId: string, quantity: number) => {
      if (quantity <= 0) {
        removeFromCart(productId);
        return;
      }
      setCart((prevCart) =>
        prevCart.map((item) =>
          item.productId === productId ? { ...item, quantity } : item,
        ),
      );
    },
    [removeFromCart, setCart],
  );

  // Handle quantity click to open edit modal
  const handleQuantityClick = useCallback(
    (productId: string, productName: string, currentQuantity: number) => {
      setQuantityEditModal({
        show: true,
        productId,
        productName,
        currentQuantity,
      });
      setTempQuantity(currentQuantity.toString());
      setShowQuantityNumpad(true);
      setIsInitialQuantityDefault(true);
    },
    [],
  );

  const handleQuantityNumpadClick = useCallback(
    (value: string) => {
      if (value === 'clear') {
        setTempQuantity('');
        setIsInitialQuantityDefault(false);
        return;
      }

      if (value === 'backspace') {
        setTempQuantity((prev) => {
          if (isInitialQuantityDefault) {
            setIsInitialQuantityDefault(false);
            return '';
          }
          return prev.slice(0, -1);
        });
        setIsInitialQuantityDefault(false);
        return;
      }

      if (value === '.') {
        setTempQuantity((prev) => {
          if (isInitialQuantityDefault) {
            setIsInitialQuantityDefault(false);
            return '0.';
          }
          if (!prev.includes('.')) {
            return prev + '.';
          }
          return prev;
        });
        return;
      }

      setTempQuantity((prev) => {
        if (isInitialQuantityDefault) {
          setIsInitialQuantityDefault(false);
          return value;
        }
        const next = prev + value;
        // allow integers or decimals up to 2 places
        if (/^\d*\.?\d{0,2}$/.test(next)) {
          return next;
        }
        return prev;
      });
    },
    [isInitialQuantityDefault],
  );

  // Handle quantity edit confirmation
  const handleQuantityEditConfirm = useCallback(() => {
    if (!quantityEditModal.productId) return;

    const newQuantity = parseFloat(tempQuantity);
    if (isNaN(newQuantity) || newQuantity < 0) {
      showToast('Please enter a valid quantity', 'error');
      return;
    }

    if (newQuantity === 0) {
      removeFromCart(quantityEditModal.productId);
    } else {
      updateCartQuantity(quantityEditModal.productId, newQuantity);
    }

    setQuantityEditModal({
      show: false,
      productId: null,
      productName: '',
      currentQuantity: 0,
    });
    setTempQuantity('');
    setShowQuantityNumpad(false);
    setIsInitialQuantityDefault(false);
  }, [
    quantityEditModal.productId,
    tempQuantity,
    removeFromCart,
    updateCartQuantity,
    showToast,
  ]);

  // Handle quantity edit modal close
  const handleQuantityEditClose = useCallback(() => {
    setQuantityEditModal({
      show: false,
      productId: null,
      productName: '',
      currentQuantity: 0,
    });
    setTempQuantity('');
    setShowQuantityNumpad(false);
    setIsInitialQuantityDefault(false);
  }, []);

  // Calculate totals
  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cart]);

  // Memoize formatCurrency function
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Handle customer selection
  const handleCustomerSelect = useCallback(
    (customer: Customer) => {
      setSelectedCustomer(customer);
      setCustomerSearchQuery(customer.name);
      setShowCustomerDropdown(false);
      setIsCustomerInputFocused(false);
    },
    [setSelectedCustomer, setCustomerSearchQuery],
  );

  // Handle add customer success
  const handleAddCustomerSuccess = useCallback(
    async (createdCustomer: Customer) => {
      // Reload sales options to get updated list
      await reload();
      // Automatically select the newly created customer
      // Convert to sales Customer type (which is a subset)
      const salesCustomer: Customer = {
        id: createdCustomer.id,
        name: createdCustomer.name,
        email: createdCustomer.email,
        phone: createdCustomer.phone,
        address: createdCustomer.address,
        tinNumber: createdCustomer.tinNumber,
        notes: createdCustomer.notes,
      };
      handleCustomerSelect(salesCustomer);
      showToast('Customer created successfully', 'success');
    },
    [reload, handleCustomerSelect, showToast],
  );

  // Handle add customer error
  const handleAddCustomerError = useCallback(
    (message: string) => {
      showToast(message, 'error');
    },
    [showToast],
  );

  // Handle proceed to payment
  const handleProceedToPayment = useCallback(() => {
    if (cart.length === 0) {
      showToast('Please add items to cart before proceeding', 'error');
      return;
    }
    const customerToUse = selectedCustomer || defaultCustomer;
    if (!customerToUse) {
      showToast(
        'Please select a customer before proceeding to payment',
        'error',
      );
      return;
    }
    navigate(ROUTES.POS.PAYMENT, {
      state: {
        cart,
        customerId: customerToUse.id,
        subtotal,
      },
    });
  }, [cart, selectedCustomer, defaultCustomer, navigate, showToast, subtotal]);

  // Handle save as floating order
  const handleSaveAsFloating = useCallback(async () => {
    if (!activeCompanyId) {
      showToast('No company selected', 'error');
      return;
    }
    if (cart.length === 0) {
      showToast('Please add items to cart before saving', 'error');
      return;
    }
    const customerToUse = selectedCustomer || defaultCustomer;
    if (!customerToUse) {
      showToast('Please select a customer before saving', 'error');
      return;
    }
    try {
      await storeFloatingOrder(
        cart,
        customerToUse.id,
        activeCompanyId,
        customerToUse.name,
      );
      showToast('Order saved as floating', 'success');
      // Clear cart after saving
      setCart([]);
      setSelectedCustomer(null);
      setCustomerSearchQuery('');
    } catch {
      showToast('Failed to save floating order', 'error');
    }
  }, [
    activeCompanyId,
    cart,
    selectedCustomer,
    defaultCustomer,
    showToast,
    setCart,
    setSelectedCustomer,
    setCustomerSearchQuery,
  ]);

  // Load orders lists
  const loadOrders = useCallback(
    async (type: 'floating' | 'voided' | 'completed') => {
      if (!activeCompanyId) {
        return;
      }
      try {
        if (type === 'floating') {
          const orders = await getAllFloatingOrders(activeCompanyId);
          setFloatingOrders(orders);
        } else if (type === 'voided') {
          const orders = await getAllVoidedOrders(activeCompanyId);
          setVoidedOrders(orders);
        } else if (type === 'completed') {
          const orders = await getAllCompletedOrders(activeCompanyId);
          // Sync completed order statuses with their pending transactions
          // This ensures sync status is accurate even if sync happened in background
          const syncedOrders = await Promise.all(
            orders.map(async (order) => {
              try {
                const pendingTransaction = await getPendingTransaction(
                  order.id,
                );
                if (
                  pendingTransaction &&
                  pendingTransaction.syncStatus !== order.syncStatus
                ) {
                  // Update completed order to match pending transaction status
                  await updateCompletedOrderSyncStatus(
                    order.id,
                    pendingTransaction.syncStatus,
                    pendingTransaction.serverId,
                    pendingTransaction.error,
                  );
                  return {
                    ...order,
                    syncStatus: pendingTransaction.syncStatus,
                    serverId: pendingTransaction.serverId,
                    syncedAt: pendingTransaction.syncedAt,
                    error: pendingTransaction.error,
                  };
                }
              } catch {
                // If pending transaction doesn't exist or update fails, use original order
              }
              return order;
            }),
          );
          setCompletedOrders(syncedOrders);
        }
      } catch {
        showToast('Failed to load orders', 'error');
      }
    },
    [activeCompanyId, showToast],
  );

  // Handle show orders modal
  const handleShowOrdersModal = useCallback(
    (type: 'floating' | 'voided' | 'completed') => {
      setShowOrdersModal({ show: true, type });
      void loadOrders(type);
    },
    [loadOrders],
  );

  // Handle continue floating order
  const handleContinueFloatingOrder = useCallback(
    async (order: FloatingOrder) => {
      try {
        // Delete the floating order since it's now being used
        await deleteFloatingOrder(order.id);
        // Reload floating orders list to update UI
        await loadOrders('floating');
        // Load the order into cart
        setCart(order.cart);
        const customer = customers.find((c) => c.id === order.customerId);
        if (customer) {
          setSelectedCustomer(customer);
          setCustomerSearchQuery(customer.name);
        }
        setShowOrdersModal({ show: false, type: 'floating' });
        setShowCart(true);
        showToast('Floating order loaded', 'success');
      } catch {
        showToast('Failed to load floating order', 'error');
      }
    },
    [
      customers,
      loadOrders,
      showToast,
      setCart,
      setCustomerSearchQuery,
      setSelectedCustomer,
    ],
  );

  // Handle void order
  const handleVoidOrder = useCallback(
    async (order: FloatingOrder | CompletedOrder) => {
      if (!activeCompanyId) {
        showToast('No company selected', 'error');
        return;
      }
      try {
        // For floating orders, convert to voided order
        if ('cart' in order && !('completedAt' in order)) {
          const floatingOrder: FloatingOrder = order;
          // Convert floating order to CreateSalesRequest format
          const today = new Date().toISOString().split('T')[0];
          const requestData: CreateSalesRequest = {
            date: today,
            customerId: floatingOrder.customerId,
            productEntries: floatingOrder.cart.map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
            })),
            cashOrArEntries: [
              {
                categoryId: '', // Floating orders don't have payment method yet
                amount: floatingOrder.subtotal,
              },
            ],
          };
          // Create a pending transaction structure for voiding
          const pendingTransaction: PendingTransaction = {
            id: floatingOrder.id,
            companyId: floatingOrder.companyId,
            syncStatus: 'pending_sync',
            requestData,
            createdAt: floatingOrder.createdAt,
          };
          await storeVoidedOrder(pendingTransaction, 'Voided floating order');
          // Remove from floating orders
          await deleteFloatingOrder(floatingOrder.id);
          showToast('Floating order voided', 'success');
          await loadOrders('voided');
          await loadOrders('floating');
        } else if ('completedAt' in order) {
          // For completed orders, move to voided orders
          const completedOrder = order as CompletedOrder;
          const pendingTransaction: PendingTransaction = {
            id: completedOrder.id,
            companyId: completedOrder.companyId,
            syncStatus: completedOrder.syncStatus,
            requestData: completedOrder.requestData,
            serverId: completedOrder.serverId,
            createdAt: completedOrder.completedAt,
            syncedAt: completedOrder.syncedAt,
            error: completedOrder.error,
          };
          await storeVoidedOrder(pendingTransaction, 'Voided by user');
          // Remove from completed orders
          await deleteCompletedOrder(completedOrder.id);
          showToast('Order voided', 'success');
          await loadOrders('voided');
          await loadOrders('completed');
        }
      } catch {
        showToast('Failed to void order', 'error');
      }
    },
    [activeCompanyId, loadOrders, showToast],
  );

  // Check if prerequisites are met (customers can be created dynamically, so skip check)
  const hasPaymentMethods = paymentMethods.length > 0;
  const hasProducts = products.length > 0;
  const isSetupIncomplete = useMemo(() => {
    return !loading && (!hasPaymentMethods || !hasProducts);
  }, [loading, hasPaymentMethods, hasProducts]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  if (isSetupIncomplete) {
    return (
      <div className={styles.container}>
        <div className={styles.setupErrorState}>
          <div className={styles.prerequisitesCard}>
            <h2 className={styles.prerequisitesTitle}>
              Before you can use the POS
            </h2>
            <p className={styles.prerequisitesDescription}>
              Please setup the checklist to get started using the POS:
            </p>
            <ul className={styles.prerequisitesList}>
              <li className={styles.prerequisitesItem}>
                {hasPaymentMethods ? (
                  <CheckCircle2 size={20} className={styles.checkIcon} />
                ) : (
                  <Circle size={20} className={styles.circleIcon} />
                )}
                <div className={styles.prerequisitesItemContent}>
                  <div className={styles.prerequisitesItemTitle}>
                    <CreditCard size={18} />
                    <span>Set up Payment Methods</span>
                  </div>
                  {!hasPaymentMethods && (
                    <p className={styles.prerequisitesItemDescription}>
                      You need at least one payment method to record how
                      customers pay for their purchases (e.g., Cash, Credit
                      Card, Bank Transfer).
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(ROUTES.DASHBOARD)}
                    className={styles.prerequisitesButton}
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </li>
              <li className={styles.prerequisitesItem}>
                {hasProducts ? (
                  <CheckCircle2 size={20} className={styles.checkIcon} />
                ) : (
                  <Circle size={20} className={styles.circleIcon} />
                )}
                <div className={styles.prerequisitesItemContent}>
                  <div className={styles.prerequisitesItemTitle}>
                    <Package size={18} />
                    <span>Set up Products or Services</span>
                  </div>
                  {!hasProducts && (
                    <p className={styles.prerequisitesItemDescription}>
                      You need at least one product or service to sell. Products
                      are items you sell, and services are non-physical
                      offerings.
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(ROUTES.PRODUCTS.ROOT)}
                    className={styles.prerequisitesButton}
                  >
                    Go to Products
                  </Button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showCart && (
        <div
          className={styles.cartOverlay}
          onClick={() => setShowCart(false)}
        />
      )}
      <div className={styles.content}>
        {/* Products Section */}
        <div className={styles.productsSection}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              autoComplete="off"
            />
            <div className={styles.actionButtons}>
              <button
                type="button"
                className={styles.reviewOrderButton}
                onClick={() => setShowCart(true)}
                disabled={cart.length === 0}
              >
                <ShoppingCart size={20} />
                Review Order {cart.length > 0 && `(${cart.length})`}
              </button>
              {/* Desktop: Show individual buttons */}
              <div className={styles.ordersButtonsRow}>
                <button
                  type="button"
                  className={styles.ordersButton}
                  onClick={() => handleShowOrdersModal('floating')}
                  title="Floating Orders"
                >
                  <Clock size={20} />
                </button>
                <button
                  type="button"
                  className={styles.ordersButton}
                  onClick={() => handleShowOrdersModal('voided')}
                  title="Voided Orders"
                >
                  <Ban size={20} />
                </button>
                <button
                  type="button"
                  className={styles.ordersButton}
                  onClick={() => handleShowOrdersModal('completed')}
                  title="Completed Orders"
                >
                  <CheckSquare size={20} />
                </button>
                <button
                  type="button"
                  className={styles.ordersButton}
                  onClick={() => setShowPrinterQuickSettings(true)}
                  title="Printer Settings"
                >
                  <Printer size={20} />
                </button>
              </div>
              {/* Mobile: Show square button */}
              <button
                type="button"
                className={styles.ordersSquareButton}
                onClick={() => setShowOrderTypeModal(true)}
                title="View Orders"
              >
                <History size={20} />
              </button>
              <button
                type="button"
                className={styles.ordersSquareButton}
                onClick={() => setShowPrinterQuickSettings(true)}
                title="Printer Settings"
              >
                <Printer size={20} />
              </button>
            </div>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className={styles.categoryFilter}>
              <button
                type="button"
                className={`${styles.categoryButton} ${
                  selectedCategory === null ? styles.categoryButtonActive : ''
                }`}
                onClick={() => setSelectedCategory(null)}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`${styles.categoryButton} ${
                    selectedCategory === category
                      ? styles.categoryButtonActive
                      : ''
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          <div className={styles.productsContainer}>
            {filteredProducts.length === 0 ? (
              <div className={styles.emptyState}>
                {searchQuery
                  ? 'No products found matching your search'
                  : selectedCategory
                    ? `No products in "${selectedCategory}"`
                    : 'No products available'}
              </div>
            ) : (
              Object.entries(groupedProducts).map(
                ([categoryName, categoryProducts]) => (
                  <div key={categoryName} className={styles.categoryGroup}>
                    <div className={styles.categoryGroupHeader}>
                      {categoryName}
                    </div>
                    <div className={styles.productsGrid}>
                      {categoryProducts.map((product) => {
                        const price =
                          typeof product.price === 'string'
                            ? Number(product.price)
                            : product.price;
                        const cartItem = cart.find(
                          (item) => item.productId === product.id,
                        );
                        const quantity = cartItem?.quantity || 0;

                        return (
                          <div
                            key={product.id}
                            className={styles.productCardWrapper}
                          >
                            <button
                              className={styles.productCard}
                              onClick={() => addToCart(product)}
                              type="button"
                            >
                              <div className={styles.productName}>
                                {product.name}
                              </div>
                              <div className={styles.productPrice}>
                                {formatCurrency(price)}
                              </div>
                            </button>
                            {quantity > 0 && (
                              <div className={styles.productQuantityControls}>
                                <button
                                  type="button"
                                  className={styles.productQuantityButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateCartQuantity(
                                      product.id,
                                      quantity - 1,
                                    );
                                  }}
                                >
                                  <Minus size={14} />
                                </button>
                                <span
                                  className={styles.productQuantity}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuantityClick(
                                      product.id,
                                      product.name,
                                      quantity,
                                    );
                                  }}
                                >
                                  {quantity}
                                </span>
                                <button
                                  type="button"
                                  className={styles.productQuantityButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateCartQuantity(
                                      product.id,
                                      quantity + 1,
                                    );
                                  }}
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ),
              )
            )}
          </div>
        </div>

        {/* Cart Section */}
        <div
          className={`${styles.cartSection} ${
            showCart ? styles.cartVisible : ''
          }`}
        >
          <div className={styles.cartHeader}>
            <ShoppingCart size={20} />
            <h2>Cart</h2>
            <button
              type="button"
              className={styles.closeCartButton}
              onClick={() => setShowCart(false)}
            >
              <X size={20} />
            </button>
          </div>

          {/* Customer Search */}
          <div className={styles.customerSearchContainer}>
            <label
              htmlFor="customerSearch"
              className={styles.customerSearchLabel}
            >
              Customer
            </label>
            <div
              className={styles.customerSearchWrapper}
              ref={customerSearchRef}
            >
              <input
                id="customerSearch"
                type="text"
                placeholder="Search customer..."
                value={customerSearchQuery}
                onChange={(e) => {
                  setCustomerSearchQuery(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => {
                  setIsCustomerInputFocused(true);
                  // If default customer is selected, clear input for immediate typing
                  if (
                    defaultCustomer &&
                    selectedCustomer?.id === defaultCustomer.id
                  ) {
                    setCustomerSearchQuery('');
                  }
                  setShowCustomerDropdown(true);
                }}
                onBlur={(e) => {
                  // Don't close if clicking on dropdown item
                  if (
                    customerSearchRef.current &&
                    customerSearchRef.current.contains(e.relatedTarget as Node)
                  ) {
                    return;
                  }
                  // Delay to allow click events to fire
                  setTimeout(() => {
                    setIsCustomerInputFocused(false);
                  }, 200);
                }}
                className={styles.customerSearchInput}
                autoComplete="off"
              />
              {showCustomerDropdown && (
                <div className={styles.customerDropdown}>
                  {filteredCustomers.length > 0 ? (
                    <>
                      {filteredCustomers.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          className={styles.customerDropdownItem}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent input blur
                            handleCustomerSelect(customer);
                          }}
                        >
                          <div className={styles.customerName}>
                            {customer.name}
                          </div>
                          <div className={styles.customerEmail}>
                            {customer.email || 'No email'}
                          </div>
                        </button>
                      ))}
                      <div className={styles.customerDropdownDivider} />
                    </>
                  ) : null}
                  <button
                    type="button"
                    className={styles.addCustomerButton}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                      setShowAddCustomerModal(true);
                      setShowCustomerDropdown(false);
                      setIsCustomerInputFocused(false);
                    }}
                  >
                    <Plus size={16} />
                    Add New Customer
                  </button>
                </div>
              )}
            </div>
          </div>

          {cart.length === 0 ? (
            <div className={styles.emptyCart}>Cart is empty</div>
          ) : (
            <>
              <div className={styles.cartItems}>
                {cart.map((item) => (
                  <div key={item.productId} className={styles.cartItem}>
                    <div className={styles.cartItemInfo}>
                      <div className={styles.cartItemName}>
                        {item.productName}
                      </div>
                      <div className={styles.cartItemPrice}>
                        {formatCurrency(item.price)} × {item.quantity}
                      </div>
                    </div>
                    <div className={styles.cartItemActions}>
                      <button
                        type="button"
                        className={styles.quantityButton}
                        onClick={() =>
                          updateCartQuantity(item.productId, item.quantity - 1)
                        }
                      >
                        <Minus size={16} />
                      </button>
                      <span
                        className={styles.quantity}
                        onClick={() =>
                          handleQuantityClick(
                            item.productId,
                            item.productName,
                            item.quantity,
                          )
                        }
                      >
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        className={styles.quantityButton}
                        onClick={() =>
                          updateCartQuantity(item.productId, item.quantity + 1)
                        }
                      >
                        <Plus size={16} />
                      </button>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removeFromCart(item.productId)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.cartSummary}>
                <div className={styles.summaryRow}>
                  <span>Subtotal:</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className={styles.summaryRowTotal}>
                  <span>Total:</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
              </div>

              <div className={styles.cartActions}>
                <Button
                  variant="outline"
                  onClick={() => {
                    void handleSaveAsFloating();
                  }}
                  className={styles.saveFloatingButton}
                >
                  Hold
                </Button>
                <Button
                  onClick={handleProceedToPayment}
                  className={styles.paymentButton}
                >
                  Proceed to Payment
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Quantity Numpad Modal */}
      <Modal
        show={quantityEditModal.show && showQuantityNumpad}
        onHide={handleQuantityEditClose}
        centered
        className={styles.numpadModal}
      >
        <Modal.Body className={styles.numpadModalBody}>
          <div
            className={`${styles.numpadAmountDisplay} ${
              isInitialQuantityDefault ? styles.numpadAmountDisplayDefault : ''
            }`}
          >
            {tempQuantity === '' ? '0' : tempQuantity}
          </div>
          <div className={styles.numpad}>
            <div className={styles.numpadRow}>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('1')}
              >
                1
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('2')}
              >
                2
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('3')}
              >
                3
              </button>
            </div>
            <div className={styles.numpadRow}>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('4')}
              >
                4
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('5')}
              >
                5
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('6')}
              >
                6
              </button>
            </div>
            <div className={styles.numpadRow}>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('7')}
              >
                7
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('8')}
              >
                8
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('9')}
              >
                9
              </button>
            </div>
            <div className={styles.numpadRow}>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('.')}
              >
                .
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('0')}
              >
                0
              </button>
              <button
                type="button"
                className={styles.numpadButton}
                onClick={() => handleQuantityNumpadClick('backspace')}
              >
                ⌫
              </button>
            </div>
            <div className={styles.numpadRow}>
              <button
                type="button"
                className={`${styles.numpadButton} ${styles.numpadClearButton}`}
                onClick={() => handleQuantityNumpadClick('clear')}
              >
                Clear
              </button>
              <button
                type="button"
                className={`${styles.numpadButton} ${styles.numpadCancelButton}`}
                onClick={handleQuantityEditClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.numpadButton} ${styles.numpadOkButton}`}
                onClick={handleQuantityEditConfirm}
              >
                OK
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Customer Modal */}
      <AddCustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onSuccess={(customer) => {
          handleAddCustomerSuccess(customer).catch(() => {
            // Error handling is done in handleAddCustomerSuccess
          });
        }}
        onError={handleAddCustomerError}
        initialName={customerSearchQuery.trim()}
      />

      {/* Order Type Selection Modal */}
      <OrderTypeSelectionModal
        show={showOrderTypeModal}
        onHide={() => setShowOrderTypeModal(false)}
        onSelect={(type) => {
          handleShowOrdersModal(type);
        }}
      />

      {/* Orders List Modals */}
      <OrdersListModal
        show={showOrdersModal.show && showOrdersModal.type === 'floating'}
        onHide={() => setShowOrdersModal({ show: false, type: 'floating' })}
        orderType="floating"
        orders={floatingOrders}
        onContinue={(order) => {
          void handleContinueFloatingOrder(order);
        }}
        onVoid={(order) => {
          void handleVoidOrder(order);
        }}
      />
      <OrdersListModal
        show={showOrdersModal.show && showOrdersModal.type === 'voided'}
        onHide={() => setShowOrdersModal({ show: false, type: 'voided' })}
        orderType="voided"
        orders={voidedOrders}
      />
      <OrdersListModal
        show={showOrdersModal.show && showOrdersModal.type === 'completed'}
        onHide={() => setShowOrdersModal({ show: false, type: 'completed' })}
        orderType="completed"
        orders={completedOrders}
        onVoid={(order) => {
          void handleVoidOrder(order);
        }}
      />

      {/* Printer Quick Settings Modal */}
      <Modal
        show={showPrinterQuickSettings}
        onHide={() => setShowPrinterQuickSettings(false)}
        centered
        className={styles.printerModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Printer Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.printerQuickSettings}>
            <div className={styles.printerStatus}>
              <div className={styles.printerStatusLabel}>Status:</div>
              <div className={styles.printerStatusValue}>
                {savedPrinter ? (
                  <span className={styles.connectedText}>
                    {savedPrinter.name} (
                    {(activeDevice as BluetoothDevice)?.gatt?.connected ||
                    (activeDevice as USBDevice)?.opened
                      ? 'Connected'
                      : 'Disconnected'}
                    )
                  </span>
                ) : (
                  <span className={styles.disconnectedText}>
                    No printer saved
                  </span>
                )}
              </div>
            </div>

            <div className={styles.settingItem}>
              <Form.Check
                type="switch"
                id="enable-printing-quick"
                label="Enable Printing"
                checked={printerSettings.enabled}
                onChange={(e) =>
                  updatePrinterSettings({ enabled: e.target.checked })
                }
              />
            </div>

            <div className={styles.settingItem}>
              <Form.Check
                type="switch"
                id="auto-cut-quick"
                label="Paper Cutter (Auto-cut)"
                checked={printerSettings.autoCut}
                onChange={(e) =>
                  updatePrinterSettings({ autoCut: e.target.checked })
                }
              />
            </div>

            <div className={styles.printerModalConnection}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void handleConnectBluetooth();
                }}
                className={styles.connectBtn}
              >
                <Bluetooth size={18} />
                Connect Bluetooth Printer
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void handleConnectUsb();
                }}
                className={styles.connectBtn}
              >
                <Usb size={18} />
                Connect USB Printer
              </Button>
            </div>

            <div className={styles.printerModalActions}>
              <Button
                variant="outline"
                onClick={() => {
                  void handleQuickTestPrint();
                }}
                disabled={!savedPrinter || isTestPrinting}
                className={styles.modalActionBtn}
              >
                <Printer size={18} className={styles.btnIcon} />
                {isTestPrinting ? 'Printing...' : 'Test Print'}
              </Button>
              {savedPrinter && (
                <Button
                  variant="outline"
                  onClick={handleForgetPrinter}
                  className={`${styles.modalActionBtn} ${styles.forgetBtn}`}
                >
                  <Trash2 size={18} className={styles.btnIcon} />
                  Forget
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => navigate(ROUTES.POS.SETTINGS)}
                className={styles.modalActionBtn}
              >
                Full Setup
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default POS;
